import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlatformProvider } from "@/context/PlatformContext";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Studio from "@/pages/Studio";
import CreateProject from "@/pages/CreateProject";
import ProjectDetail from "@/pages/ProjectDetail";
import Templates from "@/pages/Templates";
import Branding from "@/pages/Branding";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Admin from "@/pages/Admin";
import Settings from "@/pages/Settings";
import AICopy from "@/pages/AICopy";
import AIBlog from "@/pages/AIBlog";
import AISocial from "@/pages/AISocial";
import AITranslate from "@/pages/AITranslate";
import AISeries from "@/pages/AISeries";
import Calendar from "@/pages/Calendar";
import Team from "@/pages/Team";
import Analytics from "@/pages/Analytics";
import DocsApi from "@/pages/DocsApi";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import Disclaimer from "@/pages/legal/Disclaimer";
import DPA from "@/pages/legal/DPA";
import Layout from "@/components/Layout";

function Protected({ children, ownerOnly = false }) {
  const { user, loading, isOwner } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400 font-mono-tech text-xs">
        Loading studio…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (ownerOnly && !isOwner) return <Navigate to="/" replace />;
  // Onboarding gate: if user has not completed the wizard, force them there.
  if (!user.onboarded && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  return <Layout>{children}</Layout>;
}

function OnboardingRoute() {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400 font-mono-tech text-xs">
        Loading studio…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarded) return <Navigate to="/" replace />;
  return <Onboarding />;
}

export default function App() {
  // Watch the html[data-theme] attribute so the Toaster (and any other global
  // theme-aware widget) always renders in the currently active theme.
  const [theme, setTheme] = useState(() =>
    document.documentElement.getAttribute("data-theme") || "dark"
  );
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setTheme(document.documentElement.getAttribute("data-theme") || "dark");
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  return (
    <div className="App co-grain">
      <PlatformProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/docs/api" element={<DocsApi />} />
              <Route path="/legal/privacy" element={<PrivacyPolicy />} />
              <Route path="/legal/cookies" element={<CookiePolicy />} />
              <Route path="/legal/disclaimer" element={<Disclaimer />} />
              <Route path="/legal/dpa" element={<DPA />} />
              <Route path="/onboarding" element={<OnboardingRoute />} />
              <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
              <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
              <Route path="/" element={<Protected><Studio /></Protected>} />
              <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
              <Route path="/projects/new" element={<Protected><CreateProject /></Protected>} />
              <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
              <Route path="/templates" element={<Protected><Templates /></Protected>} />
              <Route path="/branding" element={<Protected><Branding /></Protected>} />
              <Route path="/pricing" element={<Protected><Pricing /></Protected>} />
              <Route path="/settings" element={<Protected><Settings /></Protected>} />
              <Route path="/ai/copy" element={<Protected><AICopy /></Protected>} />
              <Route path="/ai/blog" element={<Protected><AIBlog /></Protected>} />
              <Route path="/ai/social" element={<Protected><AISocial /></Protected>} />
              <Route path="/ai/translate" element={<Protected><AITranslate /></Protected>} />
              <Route path="/ai/series" element={<Protected><AISeries /></Protected>} />
              <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
              <Route path="/team" element={<Protected><Team /></Protected>} />
              <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
              <Route path="/admin/*" element={<Protected ownerOnly><Admin /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton theme={theme} />
        </AuthProvider>
      </PlatformProvider>
    </div>
  );
}
